import { type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';

type DateMode = 'calendar' | 'business';

type HonariosForm = {
  baseValue: string;
  feePercent: string;
  entryValue: string;
  installmentCount: string;
};

type ParcelamentoForm = {
  totalValue: string;
  entryValue: string;
  installmentCount: string;
};

type PrazoForm = {
  startDate: string;
  days: string;
  mode: DateMode;
};

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const toNumber = (value: string) => {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  if (!normalized) {
    return 0;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value: number) => moneyFormatter.format(Number.isFinite(value) ? value : 0);

const clampInstallments = (value: number) => {
  const count = Math.floor(Number.isFinite(value) ? value : 0);
  return count > 0 ? count : 0;
};

const addBusinessDays = (date: Date, days: number) => {
  const next = new Date(date.getTime());
  let remaining = Math.max(0, Math.floor(days));

  while (remaining > 0) {
    next.setDate(next.getDate() + 1);
    const day = next.getDay();
    if (day !== 0 && day !== 6) {
      remaining -= 1;
    }
  }

  return next;
};

const getIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const emptyHonarios: HonariosForm = {
  baseValue: '',
  feePercent: '',
  entryValue: '',
  installmentCount: '',
};

const emptyParcelamento: ParcelamentoForm = {
  totalValue: '',
  entryValue: '',
  installmentCount: '',
};

const emptyPrazo: PrazoForm = {
  startDate: '',
  days: '',
  mode: 'calendar',
};

const CalculatorsPage: FC = () => {
  const [honarios, setHonarios] = useState<HonariosForm>(emptyHonarios);
  const [parcelamento, setParcelamento] = useState<ParcelamentoForm>(emptyParcelamento);
  const [prazo, setPrazo] = useState<PrazoForm>(emptyPrazo);

  const honariosResult = useMemo(() => {
    const baseValue = toNumber(honarios.baseValue);
    const feePercent = toNumber(honarios.feePercent);
    const entryValue = Math.max(0, toNumber(honarios.entryValue));
    const installmentCount = clampInstallments(toNumber(honarios.installmentCount));
    const feeValue = (baseValue * feePercent) / 100;
    const entry = Math.min(entryValue, feeValue);
    const balanceAfterEntry = Math.max(feeValue - entry, 0);
    const installmentValue = installmentCount > 0 ? balanceAfterEntry / installmentCount : 0;

    return {
      feeValue,
      balanceAfterEntry,
      installmentValue,
    };
  }, [honarios]);

  const parcelamentoResult = useMemo(() => {
    const totalValue = toNumber(parcelamento.totalValue);
    const entryValue = Math.max(0, toNumber(parcelamento.entryValue));
    const installmentCount = clampInstallments(toNumber(parcelamento.installmentCount));
    const entry = Math.min(entryValue, totalValue);
    const balance = Math.max(totalValue - entry, 0);
    const installmentValue = installmentCount > 0 ? balance / installmentCount : 0;

    return {
      balance,
      installmentValue,
    };
  }, [parcelamento]);

  const prazoResult = useMemo(() => {
    if (!prazo.startDate || !prazo.days) {
      return '';
    }

    const start = new Date(`${prazo.startDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) {
      return '';
    }

    const days = Math.max(0, Math.floor(toNumber(prazo.days)));
    const endDate =
      prazo.mode === 'business'
        ? addBusinessDays(start, days)
        : new Date(start.getTime() + days * 24 * 60 * 60 * 1000);

    return getIsoDate(endDate);
  }, [prazo]);

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-foreground-900">Calculadoras</h2>
          <p className="text-sm text-foreground-500">
            Ferramentas internas estimativas para apoiar a rotina do escritório.
          </p>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Cálculos internos estimativos. Conferir regras legais, jurisprudenciais e dados do caso concreto antes de usar.
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <section className="rounded-2xl border border-background-200 bg-background-50 p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground-800">Honorários simples</h3>
                <p className="text-sm text-foreground-500 mt-0.5">Estimativa básica com entrada e parcelamento.</p>
              </div>
              <button
                type="button"
                onClick={() => setHonarios(emptyHonarios)}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
              >
                Limpar
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Valor base</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={honarios.baseValue}
                  onChange={(event) => setHonarios((previous) => ({ ...previous, baseValue: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0,00"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Percentual de honorários</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={honarios.feePercent}
                  onChange={(event) => setHonarios((previous) => ({ ...previous, feePercent: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Entrada</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={honarios.entryValue}
                  onChange={(event) => setHonarios((previous) => ({ ...previous, entryValue: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0,00"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Número de parcelas</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={honarios.installmentCount}
                  onChange={(event) => setHonarios((previous) => ({ ...previous, installmentCount: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-[11px] text-foreground-500">Valor dos honorários</p>
                <p className="text-base font-semibold text-foreground-800 mt-1">{formatMoney(honariosResult.feeValue)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-[11px] text-foreground-500">Saldo após entrada</p>
                <p className="text-base font-semibold text-foreground-800 mt-1">{formatMoney(honariosResult.balanceAfterEntry)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-[11px] text-foreground-500">Valor da parcela</p>
                <p className="text-base font-semibold text-foreground-800 mt-1">{formatMoney(honariosResult.installmentValue)}</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-background-200 bg-background-50 p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground-800">Parcelamento simples</h3>
                <p className="text-sm text-foreground-500 mt-0.5">Divisão direta do saldo por número de parcelas.</p>
              </div>
              <button
                type="button"
                onClick={() => setParcelamento(emptyParcelamento)}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
              >
                Limpar
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Valor total</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={parcelamento.totalValue}
                  onChange={(event) => setParcelamento((previous) => ({ ...previous, totalValue: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0,00"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Entrada</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={parcelamento.entryValue}
                  onChange={(event) => setParcelamento((previous) => ({ ...previous, entryValue: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0,00"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Número de parcelas</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={parcelamento.installmentCount}
                  onChange={(event) => setParcelamento((previous) => ({ ...previous, installmentCount: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-[11px] text-foreground-500">Saldo</p>
                <p className="text-base font-semibold text-foreground-800 mt-1">{formatMoney(parcelamentoResult.balance)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-3">
                <p className="text-[11px] text-foreground-500">Valor da parcela</p>
                <p className="text-base font-semibold text-foreground-800 mt-1">{formatMoney(parcelamentoResult.installmentValue)}</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-background-200 bg-background-50 p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground-800">Prazo simples</h3>
                <p className="text-sm text-foreground-500 mt-0.5">Estimativa de data final em dias corridos ou úteis.</p>
              </div>
              <button
                type="button"
                onClick={() => setPrazo(emptyPrazo)}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
              >
                Limpar
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Data inicial</span>
                <input
                  type="date"
                  value={prazo.startDate}
                  onChange={(event) => setPrazo((previous) => ({ ...previous, startDate: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Quantidade de dias</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={prazo.days}
                  onChange={(event) => setPrazo((previous) => ({ ...previous, days: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Tipo</span>
                <select
                  value={prazo.mode}
                  onChange={(event) => setPrazo((previous) => ({ ...previous, mode: event.target.value as DateMode }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                >
                  <option value="calendar">Dias corridos</option>
                  <option value="business">Dias úteis</option>
                </select>
              </label>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-3">
              <p className="text-[11px] text-foreground-500">Data final estimada</p>
              <p className="text-base font-semibold text-foreground-800 mt-1">
                {prazoResult ? new Date(`${prazoResult}T00:00:00`).toLocaleDateString('pt-BR') : '—'}
              </p>
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
};

export default CalculatorsPage;
